<template>
    <LayoutBase class="identity-info-edit">
        <van-form class="my-info-form" validate-first @submit="onSubmit" ref="form">
            <template v-if="!showNationalityType || !isForeign">
                <EnumPicker
                    :label="$t('common.base.identityType')"
                    :options="$constants.IDENTITY_TYPES"
                    i18n-prefix="common.identityTypeEnum"
                    v-model="form.identityType"
                    @change="onIdentityTypeChange"
                    class="identity-type"
                    :rules="formRules.required"
                />
                <van-field
                    :label="$t('common.base.identityNo')"
                    v-model="form.identityNo"
                    :placeholder="$t('common.placeholder.input')"
                    :rules="formRules.identityNo"
                    :maxlength="20"
                    :error="false"
                    :error-message="formErrors.identityNo"
                    name="identityNo"
                    @blur="doSyncBirthday()"
                />
                <van-field
                    :label="$t('common.base.ethnicity')"
                    v-model="form.ethnicity"
                    :placeholder="$t('common.placeholder.input')"
                    :rules="formRules.required"
                    :error="false"
                    :maxlength="20"
                />
                <DateTimePicker
                    :label="$t('common.base.birthday')"
                    :rules="formRules.required"
                    v-model="form.birthday"
                />
                <EnumPicker
                    :label="$t('common.base.politicalAffiliation')"
                    :options="$constants.POLITICAL_AFFILIATION_TYPES"
                    :rules="formRules.required"
                    i18n-prefix="common.politicalAffiliationEnum"
                    v-model="form.politicalAffiliation"
                />
                <EnumPicker
                    :label="$t('common.base.householdType')"
                    :options="$constants.HOUSEHOLD_TYPES"
                    :rules="formRules.required"
                    i18n-prefix="common.householdTypeEnum"
                    v-model="form.householdType"
                />
                <AreaPicker
                    :label="$t('common.base.householdLocation')"
                    :rules="formRules.required"
                    v-model="form.areaValues"
                    @change="onHouseholdLocationChange"
                />
                <van-field
                    show-word-limit
                    rows="2"
                    type="textarea"
                    :label="$t('common.base.detailedHouseholdLocation')"
                    v-model="form.detailedHouseholdLocation"
                    :placeholder="$t('common.placeholder.detailedHouseholdLocation')"
                    :rules="formRules.required"
                    :error="false"
                    :maxlength="200"
                    class="detailed-household-location"
                />
                <div class="id-card-photo-group">
                    <van-cell class="upload-message">
                        <span class="sub-message">
                            <span>1.&nbsp;{{ $t("upload.message.A") }}</span>
                            <span>2.&nbsp;{{ $t("upload.message.B") }}</span>
                            <span>3.&nbsp;{{ $t("upload.message.C") }}</span>
                            <span>4.&nbsp;{{ $t("upload.message.D") }}</span>
                        </span>
                    </van-cell>
                    <van-field center input-align="right" :label="$t('my.info.idCardFront')" class="id-card">
                        <template #input>
                            <van-uploader
                                :max-count="1"
                                :accept="uploadPolicies.mimeTypesLimit"
                                :max-size="uploadPolicies.byteSizeLimit"
                                image-fit="scale-down"
                                :show-upload="showUploadButton(idCardFront)"
                                :after-read="onIdCardAfterRead"
                                :preview-options="{ images: idCardFrontImages }"
                                @oversize="onOversize"
                                @click-preview="onClickPreview"
                                @close-preview="onClosePreview"
                                @delete="onDeleteIdentityFileKey"
                                v-model="idCardFront"
                            />
                        </template>
                    </van-field>
                    <van-field center input-align="right" :label="$t('my.info.idCardBack')" class="id-card-back">
                        <template #input>
                            <van-uploader
                                image-fit="scale-down"
                                :max-count="1"
                                :accept="uploadPolicies.mimeTypesLimit"
                                :max-size="uploadPolicies.byteSizeLimit"
                                :show-upload="showUploadButton(idCardBack)"
                                :after-read="onIdCardBackAfterRead"
                                :preview-options="{ images: idCardBackImages }"
                                @oversize="onOversize"
                                @click-preview="onClickPreview"
                                @close-preview="onClosePreview"
                                @delete="onDeleteIdentityFileKeyBack"
                                v-model="idCardBack"
                            />
                        </template>
                    </van-field>
                </div>
            </template>
            <template v-else>
                <van-field
                    :label="$t('common.identity.passportNo')"
                    v-model="form.identityNo"
                    :placeholder="$t('common.placeholder.input')"
                    :rules="formRules.identityNo"
                    :maxlength="20"
                    name="passportNo"
                />
                <CountryRegionPicker
                    :label="$t('common.identity.nationality')"
                    v-model="form.nationalityCode"
                    right-icon="arrow"
                    :rules="formRules.required"
                />
                <EnumPicker
                    :label="$t('common.identity.maritalStatus')"
                    :options="$constants.MARITAL_STATUS"
                    i18n-prefix="common.maritalStatusEnum"
                    v-model="form.maritalStatus"
                    class="marital-status"
                    right-icon="arrow"
                    :rules="formRules.required"
                />
                <DateTimePicker
                    :label="$t('common.base.birthday')"
                    :rules="formRules.required"
                    v-model="form.birthday"
                    right-icon="arrow"
                />
                <CountryRegionPicker
                    :label="$t('common.identity.countryRegion')"
                    v-model="form.countryCode"
                    right-icon="arrow"
                    :rules="formRules.required"
                />
                <van-field
                    :label="$t('common.identity.birthplace')"
                    v-model="form.birthplace"
                    :placeholder="$t('common.placeholder.input')"
                    :error="false"
                    :error-message="formErrors.identityNo"
                    name="identityNo"
                    :rules="formRules.required"
                    :maxlength="200"
                    class="birthplace"
                />
                <div class="id-card-photo-group">
                    <van-cell :title="$t('common.identity.idPhotoImg')">
                        <template #label>
                            <div class="id-card-photo-container">
                                <div class="left-container">
                                    <span>1.&nbsp;{{ $t("upload.message.B") }}</span>
                                    <span>2.&nbsp;{{ $t("upload.message.C") }}</span>
                                    <span>3.&nbsp;{{ $t("upload.message.D") }}</span>
                                </div>
                                <div class="right-container">
                                    <van-uploader
                                        image-fit="scale-down"
                                        :max-count="1"
                                        :accept="uploadPolicies.mimeTypesLimit"
                                        :max-size="uploadPolicies.byteSizeLimit"
                                        :show-upload="showUploadButton(idPhoto)"
                                        :after-read="onIdPhotoAfterRead"
                                        :preview-options="{ images: idPhotoImages }"
                                        @oversize="onOversize"
                                        @click-preview="onClickPreview"
                                        @close-preview="onClosePreview"
                                        @delete="onDeleteIdPhotoFileKey"
                                        v-model="idPhoto"
                                    />
                                </div>
                            </div>
                        </template>
                    </van-cell>
                </div>
            </template>
            <VanTabbar class="submit-tabbar">
                <van-button
                    class="submit-button"
                    block
                    type="info"
                    native-type="submit"
                    :loading="loading"
                    :disabled="submitDisabled"
                >
                    {{ $t("common.btn.commit") }}
                </van-button>
            </VanTabbar>
        </van-form>
    </LayoutBase>
</template>

<script>
import Vue from "vue";
import EnumPicker from "@/components/picker/EnumPicker.vue";
import AreaPicker from "@/components/picker/AreaPicker.vue";
import DateTimePicker from "@/components/picker/DateTimePicker.vue";
import { AttachmentCode, IdentityType, NationalityType, StorageBusinessType, MaritalType } from "@/scripts/enums";
import { currentStudentBasicInfo, currentStudentIdentityInfo, updateCurrentStudentIdentityInfo } from "@/api/my";
import _ from "lodash";
import { findAreas } from "@/components/picker/area-data-for-vant";
import UploadMixin from "@/mixins/upload-mixin";
import moment from "moment";
import CountryRegionPicker from "@/components/picker/CountryRegionPicker.vue";
import { createNamespacedHelpers } from "vuex";

const { mapGetters, mapActions } = createNamespacedHelpers("config");

export default Vue.extend({
    name: "IdentityInfoEdit",
    props: {},
    mixins: [UploadMixin],
    components: { DateTimePicker, AreaPicker, EnumPicker, CountryRegionPicker },
    data() {
        return {
            form: {
                identityType: "",
                identityNo: "",
                identityFileKey: "",
                identityFileKeyBack: "",
                idPhotoFileKey: "",
                ethnicity: "",
                birthday: "",
                politicalAffiliation: "",
                householdType: "",
                areaValues: new Array(0),
                provincial: "",
                city: "",
                district: "",
                detailedHouseholdLocation: "",
                householdLocation: "",
                avatarFileKey: "",
                nationalityType: NationalityType.DOMESTIC,
                maritalStatus: MaritalType.SINGLE,
                birthplace: "",
                nationalityCode: "",
                countryCode: "",
                idPhoto: {},
                identity: {},
                identityBack: {}
            },
            formRules: {
                required: [{ required: true, message: this.$t("common.error.required") }],
                identityNo: [
                    { required: true, message: this.$t("common.error.required") },
                    { validator: this["identityNoValidator"], message: this.$t("common.error.id_card") }
                ]
            },
            formErrors: {
                identityNo: ""
            },
            idCardFront: new Array(0),
            idCardBack: new Array(0),
            idPhoto: new Array(0),
            loading: false,
            isForeign: false,
            submitDisabled: false
        };
    },
    methods: {
        ...mapActions(["getOrgConfig"]),
        onSubmit() {
            this.form.householdLocation = this.fullHouseholdLocation;
            updateCurrentStudentIdentityInfo(this.form)
                .then(ignore => {
                    this.$toast(this.$t("common.alert.saveSuccess"));
                    this.$router.back();
                })
                .catch(errors => {
                    _.forEach(errors, error => {
                        if (error.field === "identityNo") {
                            this.formErrors.identityNo = error.message;
                        }
                    });
                });
        },
        showUploadButton(fileList) {
            return _.isEmpty(fileList);
        },
        onIdCardAfterRead(file) {
            this.idCardFront = [{ isImage: true, status: "uploading" }];
            this.submitDisabled = true;
            this["afterRead"](
                file,
                {
                    businessType: StorageBusinessType.USER,
                    prefixFileName: AttachmentCode.ID_CARD
                },
                (fileKey, downloadUrl, thumbnailUrl) => {
                    this.form.identityFileKey = fileKey;
                    this.idCardFront = [{ isImage: true, key: fileKey, url: downloadUrl, downloadUrl: downloadUrl }];
                },
                () => {
                    this.submitDisabled = false;
                }
            );
        },
        onDeleteIdentityFileKey() {
            this.form.identityFileKey = "";
        },
        onIdCardBackAfterRead(file) {
            this.idCardBack = [{ isImage: true, status: "uploading" }];
            this.submitDisabled = true;
            this["afterRead"](
                file,
                {
                    businessType: StorageBusinessType.USER,
                    prefixFileName: AttachmentCode.ID_CARD_BACK
                },
                (fileKey, downloadUrl, thumbnailUrl) => {
                    this.form.identityFileKeyBack = fileKey;
                    this.idCardBack = [{ isImage: true, key: fileKey, url: downloadUrl, downloadUrl: downloadUrl }];
                },
                () => {
                    this.submitDisabled = false;
                }
            );
        },
        onIdPhotoAfterRead(file) {
            this.idPhoto = [{ isImage: true, status: "uploading" }];
            this.submitDisabled = true;
            this["afterRead"](
                file,
                {
                    businessType: StorageBusinessType.USER,
                    prefixFileName: AttachmentCode.ID_PHOTO
                },
                (fileKey, downloadUrl, thumbnailUrl) => {
                    this.form.idPhotoFileKey = fileKey;
                    this.idPhoto = [{ isImage: true, key: fileKey, url: downloadUrl, downloadUrl: downloadUrl }];
                },
                () => {
                    this.submitDisabled = false;
                }
            );
        },
        onDeleteIdentityFileKeyBack() {
            this.form.identityFileKeyBack = "";
        },
        onDeleteIdPhotoFileKey() {
            this.form.idPhotoFileKey = "";
        },
        identityNoValidator() {
            if (this.form.identityType === IdentityType.ID_CARD) {
                return /^\d{17}[0-9xX]$/.test(this.form.identityNo);
            }
            return true;
        },
        doSyncBirthday() {
            const val = this.form.identityNo;
            if (this.form.identityType === IdentityType.ID_CARD && /^\d{14,}.*/.test(val)) {
                const date = moment(val.substring(6, 14), "YYYYMMDD");
                const birthday = date.format("YYYY-MM-DD");
                if (!birthday && this.form.birthday != birthday) {
                    this.$set(this.form, "birthday", birthday);
                }
            }
        },
        onIdentityTypeChange() {
            this.$refs.form["resetValidation"]("identityNo");
            this.$refs.form["validate"]("identityNo").catch(() => null);
        },
        onHouseholdLocationChange() {
            const [provincial, city, district] = this.form.areaValues;
            this.form.provincial = provincial["code"];
            this.form.city = city["code"];
            this.form.district = district["code"];
        },
        getPhoto(photo) {
            return _.isEmpty(photo)
                ? []
                : [
                      {
                          isImage: true,
                          url: photo.downloadUrl,
                          downloadUrl: photo.downloadUrl
                      }
                  ];
        }
    },
    computed: {
        ...mapGetters(["orgConfig"]),
        idCardBackImages() {
            return _.compact(
                _.map(this.idCardBack, resource => {
                    return resource.downloadUrl;
                })
            );
        },
        idCardFrontImages() {
            return _.compact(
                _.map(this.idCardFront, resource => {
                    return resource.downloadUrl;
                })
            );
        },
        idPhotoImages() {
            return _.compact(
                _.map(this.idPhoto, resource => {
                    return resource.downloadUrl;
                })
            );
        },
        fullHouseholdLocation() {
            if (this.areaNames || this.form.detailedHouseholdLocation) {
                return this.areaNames.concat(this.form.detailedHouseholdLocation);
            }
            return "";
        },
        areaNames() {
            return _.map(this.form.areaValues, "text").join("");
        },
        showNationalityType() {
            if (Object.keys(this.orgConfig).length == 0) {
                return false;
            }
            return !!this.orgConfig["showNationalityType"];
        }
    },
    async mounted() {
        const basicInfoResp = await currentStudentBasicInfo();
        const identityInfoResp = await currentStudentIdentityInfo();
        this.form = { ...basicInfoResp.data.data, ...identityInfoResp.data.data };
        this.isForeign = this.form.nationalityType === NationalityType.FOREIGN;
        if (this.isForeign) {
            this.$set(this.form, "identityType", IdentityType.PASSPORT);
        }
        this.$set(this.form, "identityFileKey", this.form.identity.fileKey ? this.form.identity.fileKey : "");
        this.$set(
            this.form,
            "identityFileKeyBack",
            this.form.identityBack.fileKey ? this.form.identityBack.fileKey : ""
        );
        this.$set(this.form, "idPhotoFileKey", this.form.idPhoto.fileKey ? this.form.idPhoto.fileKey : "");
        this.idCardFront = this.getPhoto(this.form.identity);
        this.idCardBack = this.getPhoto(this.form.identityBack);
        this.idPhoto = this.getPhoto(this.form.idPhoto);
        this.submitDisabled = false;
        if (this.form.provincial && this.form.city && this.form.district) {
            const areas = findAreas(this.form.provincial, this.form.city, this.form.district);
            const areaNames = _.map(areas, "text").join("");
            this.$set(this.form, "areaValues", areas);
            this.$set(this.form, "detailedHouseholdLocation", _.replace(this.form.householdLocation, areaNames, ""));
        }
        await this.getOrgConfig();
    }
});
</script>

<style scoped lang="less">
.identity-info-edit {
    height: 100%;
    /deep/ .van-field__control {
        text-align: right;
    }
    .my-info-form {
        display: flex;
        padding: 16px 8px 82px 8px;
        flex-direction: column;

        .identity-type,
        .id-card-photo-group .upload-message {
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;
        }

        .detailed-household-location,
        .id-card-photo-group .id-card-back {
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 8px;
        }

        .id-card-photo-group {
            margin-top: 8px;
            margin-bottom: 8px;

            .id-card-photo-container {
                display: flex;
                gap: 16px;
                .left-container {
                    display: flex;
                    flex-direction: column;
                }
            }

            .upload-message {
                .sub-message {
                    color: #8a909f;
                    font-size: 12px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 19.2px;
                    display: flex;
                    padding: 8px;
                    justify-content: center;
                    align-items: flex-start;
                    align-self: stretch;
                    border-radius: 3px;
                    background: #f6f7f9;
                    flex-direction: column;
                }
            }
            .id-card,
            .id-card-back {
                /deep/ .van-uploader {
                    .van-uploader__upload {
                        width: 84px;
                        height: 52.744px;
                        flex-shrink: 0;
                        border-radius: 4px;
                    }
                }
            }
        }

        .submit-tabbar {
            display: flex;
            padding: 8px;
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
            background: #fff;
            box-shadow: 0 3px 10px 0 rgba(38, 40, 51, 0.1), 0 0 1px 0 rgba(38, 40, 51, 0.1);

            .submit-button {
                width: calc(100% - 16px);
            }
        }
    }
}
</style>
